<script lang="ts" setup>
  import { ref, computed, onBeforeUnmount } from 'vue'
  import { storeToRefs } from 'pinia'

  import { useFilesStore } from '@clevver/stores/files.store'
  import { useUserStore } from '@clevver/stores/user.store'
  import { client } from '@clevver/api/clevver_client'
  import { clevverEmitter } from '@clevver/stores/event.store'
  import { formatBytes, kindFor, KIND_ICON, type Kind } from '@clevver/helpers/files'
  import { Purchase } from '@clevver/types'

  import UploadIcon from '@clevver/assets/icons/upload.svg'
  import CheckIcon from '@clevver/assets/icons/check.svg'

  type QueueState = 'ready' | 'uploading' | 'uploaded'

  interface QueuedFile {
    id: string
    file: File
    name: string
    size: number
    kind: Kind
    thumbnail: string | null
    progress: number
    state: QueueState
  }

  let sequence = 0
  const nextId = () => `upload_${sequence++}`

  const filesStore = useFilesStore()
  const userStore = useUserStore()
  const { quota } = storeToRefs(filesStore)
  const { user } = storeToRefs(userStore)

  const emit = defineEmits<{
    (e: 'complete'): void
    (e: 'close'): void
  }>()

  const files = ref<QueuedFile[]>([])
  const dragDepth = ref(0)
  const dragging = computed(() => dragDepth.value > 0)
  const phase = ref<'idle' | 'uploading' | 'complete'>('idle')
  const inputRef = ref<HTMLInputElement | null>(null)

  const incoming = computed(() => files.value.reduce((sum, f) => sum + f.size, 0))
  const used = computed(() => quota.value?.used ?? 0)
  const total = computed(() => quota.value?.total ?? 0)
  // Storage in use once this batch lands — the panel's central quantity.
  const projected = computed(() => used.value + incoming.value)

  const usedPercent = computed(() => total.value ? Math.min(100, (used.value / total.value) * 100) : 0)
  const incomingPercent = computed(() => total.value ? Math.min(100 - usedPercent.value, (incoming.value / total.value) * 100) : 0)
  const over = computed(() => total.value > 0 && projected.value > total.value)

  const busy = computed(() => phase.value === 'uploading')
  const complete = computed(() => phase.value === 'complete')
  const canUpload = computed(() =>
    files.value.length > 0 && !!quota.value && !over.value && !busy.value
  )

  const revokeThumbnail = (f: QueuedFile) => { if (f.thumbnail) { URL.revokeObjectURL(f.thumbnail) } }
  const clearQueue = () => { files.value.forEach(revokeThumbnail); files.value = [] }
  const resetFileStates = (state: QueueState) => files.value.forEach((f) => { f.state = state; f.progress = 0 })

  const addFiles = (fileList: FileList | File[] | null) => {
    const incomingFiles = Array.from(fileList || [])
    if (!incomingFiles.length) { return }

    for (const file of incomingFiles) {
      // Mirror the server's per-file ceiling so oversized files are rejected up front.
      if (user.value && file.size > user.value.max_file_size) {
        clevverEmitter.emit('requestFailed', {
          message: `${file.name} exceeds the ${formatBytes(user.value.max_file_size)} file size limit`,
        })
        continue
      }

      const kind = kindFor(file.type)
      files.value.push({
        id: nextId(),
        file,
        name: file.name,
        size: file.size,
        kind,
        thumbnail: kind === 'image' ? URL.createObjectURL(file) : null,
        progress: 0,
        state: 'ready',
      })
    }

    // Dropping more after a finished run starts a fresh batch.
    if (phase.value === 'complete') { phase.value = 'idle' }
  }

  const onInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement
    addFiles(input.files)
    input.value = ''
  }

  const removeFile = (id: string) => {
    const file = files.value.find((f) => f.id === id)
    if (file) { revokeThumbnail(file) }
    files.value = files.value.filter((f) => f.id !== id)
  }

  const clearAll = () => {
    clearQueue()
    phase.value = 'idle'
  }

  const openPicker = () => inputRef.value?.click()

  // Drag handlers — a depth counter keeps the active state stable as the pointer
  // crosses child elements inside the drop zone; `dragging` derives from it.
  const onDragEnter = (e: DragEvent) => { e.preventDefault(); dragDepth.value++ }
  const onDragOver = (e: DragEvent) => { e.preventDefault() }
  const onDragLeave = (e: DragEvent) => { e.preventDefault(); dragDepth.value = Math.max(0, dragDepth.value - 1) }
  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    dragDepth.value = 0
    if (e.dataTransfer?.files?.length) { addFiles(e.dataTransfer.files) }
  }

  const close = () => emit('close')

  const failUpload = (message: string) => {
    clevverEmitter.emit('requestFailed', { message })
    phase.value = 'idle'
    resetFileStates('ready')
  }

  const onUploadClick = async () => {
    if (!canUpload.value) { return }

    phase.value = 'uploading'
    resetFileStates('uploading')

    try {
      const response = await client.createPurchase()
      const purchase = response.data as Purchase

      // Files upload sequentially; reflect per-file progress as each one streams up.
      for (const queued of files.value) {
        await client.uploadFile(purchase.id, queued.file, (progress: number) => {
          queued.progress = progress
        })
        queued.progress = 100
        queued.state = 'uploaded'
      }

      const startResponse = await client.startPurchaseJob(purchase.id)
      if (startResponse.error) {
        failUpload(startResponse.error)
        return
      }

      phase.value = 'complete'
      emit('complete')
    } catch (error) {
      failUpload('Upload failed. Please try again.')
    }
  }

  const reset = () => {
    clearQueue()
    dragDepth.value = 0
    phase.value = 'idle'
  }

  onBeforeUnmount(() => files.value.forEach(revokeThumbnail))

  defineExpose({ reset })
</script>

<template>
  <div class="upload">
    <!-- header -->
    <div class="upload__header">
      <div>
        <h2 class="upload__heading t-serif">Upload files</h2>
        <p class="upload__subtitle">Drag &amp; drop, or browse. Encrypted in transit and at rest.</p>
      </div>
      <button type="button" class="upload__close" aria-label="Close" @click="close">×</button>
    </div>

    <!-- body -->
    <div class="upload__body">
      <!-- drop zone -->
      <div
        class="upload__dropzone"
        :class="{ 'upload__dropzone--dragging': dragging, 'upload__dropzone--compact': files.length }"
        role="button"
        tabindex="0"
        @click="openPicker"
        @keydown.enter.prevent="openPicker"
        @keydown.space.prevent="openPicker"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <input ref="inputRef" type="file" multiple class="upload__file-input" @change="onInputChange" />
        <div class="upload__dropzone-icon"><UploadIcon :size="20" /></div>
        <div class="upload__dropzone-text">
          <div class="upload__dropzone-title">
            {{ dragging ? 'Release to add' : files.length ? 'Drop more, or browse' : 'Drop files here' }}
          </div>
          <div class="upload__dropzone-subtitle">
            <!-- min-height reserves the row, so it stays put when the text clears on drag. -->
            <template v-if="!dragging">Multiple files supported · <span class="upload__browse">browse</span></template>
          </div>
        </div>
      </div>

      <!-- file queue -->
      <div v-if="files.length" class="upload__queue">
        <div class="upload__queue-header">
          <span class="upload__queue-label">
            {{ complete ? 'Uploaded' : 'Files' }}
            <span class="t-mono upload__queue-count">{{ files.length }}</span>
          </span>
          <span class="upload__queue-meta">
            <span class="t-mono upload__queue-total">{{ formatBytes(incoming) }}</span>
            <button v-if="!busy && !complete" type="button" class="upload__clear" @click="clearAll">Clear</button>
          </span>
        </div>

        <div class="upload__list">
          <div v-for="file in files" :key="file.id" class="upload__row">
            <div class="upload__row-thumbnail" :class="`kind--${file.kind}`">
              <img v-if="file.kind === 'image' && file.thumbnail" :src="file.thumbnail" alt="" />
              <component :is="KIND_ICON[file.kind]" v-else :size="16" />
            </div>

            <div class="upload__row-info">
              <div class="upload__row-name" :title="file.name">{{ file.name }}</div>
              <div class="upload__row-details">
                <span class="t-mono upload__row-size">{{ formatBytes(file.size) }}</span>
                <template v-if="file.state !== 'ready'">
                  <span class="upload__row-separator">·</span>
                  <span class="upload__row-status" :class="{ 'upload__row-status--uploaded': file.state === 'uploaded' }">{{ file.state }}</span>
                </template>
              </div>
              <div v-if="file.state !== 'ready'" class="upload__row-progress">
                <div class="upload__row-progress-fill" :style="{ width: (file.state === 'uploaded' ? 100 : file.progress) + '%' }" />
              </div>
            </div>

            <div class="upload__row-affordance">
              <button
                v-if="file.state === 'ready'"
                type="button"
                class="upload__row-remove"
                :aria-label="`Remove ${file.name}`"
                @click="removeFile(file.id)"
              >×</button>
              <CheckIcon v-else-if="file.state === 'uploaded'" :size="16" class="upload__row-check" />
            </div>
          </div>
        </div>
      </div>

      <!-- storage projection -->
      <div v-if="quota" class="upload__storage">
        <div class="upload__storage-header">
          <span class="upload__storage-label">Storage</span>
          <span class="t-mono upload__storage-meta" :class="{ 'upload__storage-meta--over': over }">
            {{ formatBytes(projected) }} / {{ formatBytes(quota.total) }}
          </span>
        </div>
        <div class="upload__storage-bar">
          <div class="upload__storage-used" :style="{ width: usedPercent + '%' }" />
          <div class="upload__storage-incoming" :class="{ 'upload__storage-incoming--over': over }" :style="{ width: incomingPercent + '%' }" />
          <div class="upload__storage-rest" />
        </div>
        <div class="upload__storage-caption">
          <span v-if="over" class="upload__storage-over">
            Over quota by <span class="t-mono">{{ formatBytes(projected - quota.total) }}</span> — remove files or add storage.
          </span>
          <span v-else class="upload__storage-free">
            <span class="t-mono upload__storage-free-value">{{ formatBytes(Math.max(0, quota.total - projected)) }}</span> free after upload.
          </span>
        </div>
      </div>
    </div>

    <!-- footer -->
    <div class="upload__footer">
      <button type="button" class="upload__cancel" @click="close">{{ complete ? 'Close' : 'Cancel' }}</button>

      <span v-if="complete" class="upload__done">
        <CheckIcon :size="16" /><span>{{ files.length }} uploaded</span>
      </span>
      <button
        v-else
        type="button"
        class="upload__submit c-button--primary"
        :class="{ 'upload__submit--dim': !files.length || over }"
        :disabled="!canUpload"
        @click="onUploadClick"
      >
        <template v-if="busy"><span class="upload__spinner" /><span>Uploading…</span></template>
        <template v-else>
          <UploadIcon :size="15" />
          <span v-if="over">Over quota</span>
          <span v-else-if="files.length">Upload {{ files.length }} file{{ files.length > 1 ? 's' : '' }} · {{ formatBytes(incoming) }}</span>
          <span v-else>Select files</span>
        </template>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // The primary action and its accents use the same variable as the topbar Upload
  // button. Defined once on the root so the panel is self-contained — it renders
  // both inside the upload modal and on the standalone /dashboard/upload route.
  .upload {
    --upload-accent: var(--c-button-primary-background);
    --upload-accent-text: var(--c-button-primary-text);

    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    color: var(--c-fg);
  }

  // ─── header ──────────────────────────────────────────────────────────
  .upload__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 20px 18px 24px;
    border-bottom: 1px solid var(--c-line);
  }
  .upload__heading {
    margin: 0;
    font-size: 28px;
    line-height: 1;
    color: var(--c-fg);
  }
  .upload__subtitle {
    margin: 8px 0 0;
    max-width: 380px;
    font-size: 12.5px;
    color: var(--c-muted);
  }
  .upload__close {
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid var(--c-line);
    background: var(--c-surface);
    color: var(--c-muted);
    font-size: 17px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.12s, border-color 0.12s, background 0.12s;
  }
  .upload__close:hover {
    color: var(--c-fg);
    border-color: var(--c-line-strong);
  }

  // ─── body ────────────────────────────────────────────────────────────
  .upload__body {
    flex: 1;
    padding: 18px 24px 6px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  .upload__body::-webkit-scrollbar { width: 10px; }
  .upload__body::-webkit-scrollbar-thumb {
    background: var(--c-line-strong);
    border-radius: 10px;
    border: 3px solid var(--c-bg);
    background-clip: padding-box;
  }
  .upload__body::-webkit-scrollbar-track { background: transparent; }

  // ─── drop zone ───────────────────────────────────────────────────────
  .upload__dropzone {
    position: relative;
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 24px 20px;
    border: 1.5px dashed var(--c-line-strong);
    border-radius: 12px;
    background: var(--c-surface);
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  .upload__dropzone--compact { padding: 15px 18px; }
  .upload__dropzone:focus-visible {
    outline: 2px solid var(--upload-accent);
    outline-offset: 2px;
  }
  .upload__dropzone--dragging {
    border-color: var(--upload-accent);
    background: color-mix(in srgb, var(--upload-accent) 7%, var(--c-surface));
  }
  .upload__file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .upload__dropzone-icon {
    flex: none;
    width: 44px;
    height: 44px;
    border-radius: 11px;
    border: 1px solid var(--c-line);
    background: var(--c-surface-2);
    color: var(--upload-accent);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .upload__dropzone-text { min-width: 0; }
  .upload__dropzone-title {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--c-fg);
  }
  .upload__dropzone-subtitle {
    margin-top: 2px;
    min-height: 16px;
    font-size: 12px;
    color: var(--c-muted);
  }
  .upload__browse {
    color: var(--upload-accent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  // ─── file queue ──────────────────────────────────────────────────────
  .upload__queue {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .upload__queue-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .upload__queue-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--c-faint);
  }
  .upload__queue-count {
    font-size: 11px;
    font-weight: 500;
    color: var(--c-muted);
    padding: 1px 6px;
    border-radius: 5px;
    border: 1px solid var(--c-line);
    background: var(--c-surface);
  }
  .upload__queue-meta {
    display: inline-flex;
    align-items: center;
    gap: 12px;
  }
  .upload__queue-total {
    font-size: 11.5px;
    color: var(--c-muted);
  }
  .upload__clear {
    border: 0;
    background: transparent;
    color: var(--c-faint);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0;
    cursor: pointer;
    transition: color 0.12s;
  }
  .upload__clear:hover { color: var(--c-fg); }

  .upload__list {
    border: 1px solid var(--c-line);
    border-radius: 11px;
    overflow: hidden;
    background: var(--c-surface);
  }
  .upload__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--c-line);
    transition: background 0.12s ease;
  }
  .upload__row:last-child { border-bottom: 0; }
  .upload__row:hover { background: var(--c-hover); }

  .upload__row-thumbnail {
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--c-line);
    background: color-mix(in srgb, var(--kind, var(--c-muted)) 14%, var(--c-surface));
    color: var(--kind, var(--c-muted));
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .upload__row-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .upload__row-info {
    flex: 1;
    min-width: 0;
  }
  .upload__row-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--c-fg);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .upload__row-details {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
  }
  .upload__row-size {
    font-size: 11px;
    color: var(--c-faint);
  }
  .upload__row-separator { color: var(--c-line-strong); }
  .upload__row-status {
    font-size: 11.5px;
    color: var(--c-muted);
  }
  .upload__row-status--uploaded { color: var(--upload-accent); }

  .upload__row-progress {
    height: 3px;
    margin-top: 6px;
    border-radius: 2px;
    overflow: hidden;
    background: var(--c-surface-2);
  }
  .upload__row-progress-fill {
    height: 100%;
    border-radius: 2px;
    background: var(--upload-accent);
    transition: width 0.18s linear;
  }

  .upload__row-remove {
    flex: none;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--c-faint);
    font-size: 15px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: color 0.12s, background 0.12s, border-color 0.12s;
  }
  .upload__row-remove:hover {
    color: var(--c-danger);
    background: var(--c-surface-2);
  }
  // Fixed-width slot so rows don't reflow as the trailing affordance swaps
  // between remove button, check, and nothing.
  .upload__row-affordance {
    flex: none;
    width: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .upload__row-check { color: var(--upload-accent); }

  // ─── storage projection ──────────────────────────────────────────────
  .upload__storage {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .upload__storage-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  .upload__storage-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--c-faint);
  }
  .upload__storage-meta {
    font-size: 11.5px;
    color: var(--c-muted);
  }
  .upload__storage-meta--over { color: var(--c-danger); }

  .upload__storage-bar {
    display: flex;
    height: 8px;
    gap: 1.5px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--c-surface-2);
  }
  .upload__storage-used {
    background: var(--c-line-strong);
    transition: width 0.3s ease;
  }
  .upload__storage-incoming {
    background: var(--upload-accent);
    transition: width 0.3s ease, background 0.2s;
  }
  .upload__storage-incoming--over { background: var(--c-danger); }
  .upload__storage-rest { flex: 1; }

  .upload__storage-caption {
    min-height: 16px;
    font-size: 12px;
  }
  .upload__storage-over { color: var(--c-danger); }
  .upload__storage-free { color: var(--c-faint); }
  .upload__storage-free-value { color: var(--c-fg); }

  // ─── footer ──────────────────────────────────────────────────────────
  .upload__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 15px 20px;
    border-top: 1px solid var(--c-line);
    background: var(--c-surface-2);
  }
  .upload__cancel {
    height: 38px;
    padding: 0 16px;
    border-radius: 10px;
    border: 1px solid var(--c-line);
    background: transparent;
    color: var(--c-muted);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.12s, border-color 0.12s, background 0.12s;
  }
  .upload__cancel:hover {
    color: var(--c-fg);
    border-color: var(--c-line-strong);
  }

  .upload__submit {
    gap: 8px;
    height: 38px;
    padding: 0 18px;
    font-size: 13px;
    white-space: nowrap;

    // Disabled covers the busy (uploading) state too, which must stay full-strength;
    // the not-ready look is carried by --dim instead of the shared disabled dim.
    &:disabled { cursor: not-allowed; opacity: 1; }
    &--dim,
    &--dim:disabled { opacity: 0.5; }
  }

  .upload__done {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--upload-accent);
    border: 1px solid color-mix(in srgb, var(--upload-accent) 40%, transparent);
    background: color-mix(in srgb, var(--upload-accent) 9%, transparent);
  }

  .upload__spinner {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    border: 2px solid currentColor;
    border-top-color: transparent;
    animation: upload-spin 0.9s linear infinite;
  }
  @keyframes upload-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .upload__spinner { animation: none; }
  }
</style>
